import type { FAQItem } from './types';

export const onboardingFAQs: FAQItem[] = [
  {
    id: 'platform-access-web-app',
    question: 'Do residents need to download a mobile app?',
    answer: `No. This is a web application. No native mobile apps are required or planned. It works in any modern browser on desktop, tablet, and mobile. A full installable, offline-capable Progressive Web App experience (home screen install, offline caching) is a design goal, not built yet.`,
    category: 'onboarding',
    featured: true,
  },
  {
    id: 'getting-started-overview',
    question: 'How do we get started with the platform?',
    answer: `Getting started follows our comprehensive 6-month onboarding process:

**Month 1: Discovery & Planning**
• Insurance procurement (cyber liability, E&O coverage)
• Operational scope analysis and requirements gathering
• Branding setup planning and custom configuration design
• Document review and data assessment

**Month 2: Front-End Development**
• UI/UX implementation with your branding
• Module activation per package tier (Silver/Gold)
• User role configuration and workflow implementation
• Custom domain setup

**Month 3: Back-End Development & Database**
• Database schema tailored to your community
• API development and security implementation
• Cloud infrastructure deployment
• Performance optimization

**Month 4: Internal QA & Hardening**
• Automated test suite run against the build
• Dependency vulnerability scanning (npm audit)
• Bug fixes and platform hardening

**Month 5: Data Integration & Migration**
• Import governing documents into the document repository
• Migrate resident data and create user accounts
• Import financial records and validate data integrity

**Month 6: Training, UAT & Launch Preparation**
• Administrator training (up to 8 hours flexible)
• User acceptance testing with your admins
• Soft launch with test group (10-20 residents)
• Platform approval for public launch

**Month 7: Go-Live & Stabilization**
• Public launch to all residents
• 30-day heightened support period begins
• Ongoing Service Agreement activates

Target timeline is 6 months onboarding, with go-live in Month 7. Maximum 8 months if delays occur.`,
    category: 'onboarding',
    featured: true,
  },
  {
    id: 'what-happens-after-contract',
    question: 'What happens immediately after we sign the contract?',
    answer: `Your onboarding begins with insurance procurement and project setup:

**Payment & Effective Date:**
• 100% setup fee due upfront (non-refundable once work begins)
• Timeline starts when payment clears (Effective Date)
• Insurance policies activated (cyber liability, E&O, general liability)

**Within First Week (Month 1 Kickoff):**
• Kickoff meeting scheduled (2-3 hours discovery session)
• Requirements gathering and operational interviews
• Branding materials requested (logo, colors, domain info)
• Governing documents collection begins

**Month 1 Deliverables:**
• Requirements document outlining platform roadmap
• Data assessment completed
• Project timeline confirmed
• Point of contact established

**Ongoing Communication:**
• Bi-weekly check-ins during development (Months 2-4)
• Weekly reviews during data migration (Month 5)
• Daily support during training and UAT (Month 6)
• Full transparency into progress at every milestone

Note: Work cannot commence until insurance policies are active (typically within 1-2 weeks of payment).`,
    category: 'onboarding',
  },
  {
    id: 'data-migration-process',
    question: 'How do you migrate our existing data and documents?',
    answer: `Data migration happens in Month 5, after internal QA and hardening:

**Why Month 5 (Not Earlier)?**
• We test with dummy data first during Months 2-4
• Internal QA and hardening in Month 4 runs the automated test suite and a dependency vulnerability scan
• Only after that QA pass do we import real resident information

**Assessment Phase (Month 1):**
• Review existing data quality and organization
• Identify cleanup needs and provide guidance
• Plan database structure based on your data types

**Migration Execution (Month 5):**
• Import governing documents (bylaws, CC&Rs, policies, minutes)
• Migrate resident roster and create user accounts
• Import financial records if provided (past 3 years recommended)
• Create administrator accounts with appropriate access levels

**Data Types Migrated:**
• Governing documents → Document repository
• Resident information → User accounts and profiles
• Financial records → Financial transparency views
• Historical data → Archived for compliance and reference

**Data You Must Provide:**
• Digital format only (PDF, Word, Excel, CSV)
• Paper documents? You must scan them first (we don't provide scanning services)
• Clean, organized spreadsheets preferred (saves time and potential extra fees)

**Data Organization Fees:**
• Included: Up to 500 files OR 5 GB, well-organized with clear naming
• Extra cost: Extensive cleanup, volume overages, OCR for scanned images

We provide a $500 assessment if you need help planning analog-to-digital conversion.`,
    category: 'onboarding',
  },
  {
    id: 'training-and-support',
    question: 'What training is provided during onboarding?',
    answer: `Comprehensive training in Month 6 ensures admins are ready for launch:

**Administrator Training (Flexible, Up to 8 Hours):**
• Live Zoom training sessions (typically 2-3 sessions)
• All sessions recorded for future reference
• Minimum 2 board/management representatives required
• Covers: Platform navigation, voting system, financial tools, communications, document management, role-specific workflows

**Training Materials Delivered:**
• Comprehensive user manual (PDF, 50-100 pages)
• Quick-start guides for each role
• Best practices handbook

A recorded video tutorial library is a design goal, not built yet; the live Zoom sessions above are recorded for your own reference in the meantime.

**Resident Onboarding (Your Responsibility):**
• We provide signup link (e.g., app.yourlandowners.org/signup)
• You distribute link to residents via email, mail, website, meetings
• Residents self-register and create accounts (no hand-holding required)
• Platform automatically handles account creation and profile setup

**30-Day Stabilization Support (Post-Launch):**
• Begins on Month 7 go-live date
• Heightened support with faster response times per your package tier
• Gold: 4-hour response for Critical issues, 12-hour for High priority
• Silver: 8-hour response for Critical issues, 24-hour for High priority
• Proactive monitoring during initial resident onboarding wave

**What Changes After 30 Days?**
• Same SLA response times continue under Ongoing Service Agreement
• Support doesn't end—only the heightened launch intensity decreases
• Monthly service fees include unlimited email/chat support during business hours

**Not Included:**
• Additional training beyond 8 hours ($150-$250/hour)
• Community change management (convincing residents to adopt platform)
• Running your events, moderating posts, or managing operations
• Hand-holding each resident through signup process`,
    category: 'onboarding',
  },
  {
    id: 'timeline-expectations',
    question: 'How long does the complete setup process take?',
    answer: `Realistic timeline: 6 months onboarding + Month 7 go-live.

**Month 1: Discovery & Preparation**
• Insurance procurement (cyber liability, E&O, general)
• Operational scope analysis and requirements gathering
• Branding setup planning and document review
• Data assessment (no real data imported yet)

**Month 2: Front-End Development**
• Build web app interface with your branding
• Configure features per package tier (Silver/Gold)
• Set up user roles and governance workflows
• Custom domain configuration

**Month 3: Back-End Development & Database**
• Database design tailored to your community
• API development and security implementation
• Cloud infrastructure deployment (AWS/Google Cloud)
• Performance optimization and testing

**Month 4: Internal QA & Hardening**
• Automated test suite run against the build
• Dependency vulnerability scanning (npm audit)
• Internal security review by the development team
• Bug fixes and platform hardening

**Month 5: Data Integration & Migration**
• Import governing documents into repository
• Migrate resident roster and create accounts
• Import financial records (if provided)
• Data validation and error correction

**Month 6: Training, UAT & Launch Preparation**
• Administrator training (up to 8 hours flexible)
• User acceptance testing with your admins
• Soft launch with test group (10-20 residents recommended)
• Final approval and go-live planning

**Month 7: Go-Live & Stabilization**
• Platform launches publicly to all residents
• 30-day heightened support period begins
• Ongoing Service Agreement activates ($10/parcel/month for Gold, $8/parcel for Silver)

**Maximum Timeline:** 8 months if delays occur (client document delays, extended UAT, training rescheduling). Timeline extends day-for-day for client-caused delays.

**What Doesn't Delay Timeline:** Holidays, ToolboxLogic-caused delays (our responsibility), force majeure events.`,
    category: 'onboarding',
  },
  {
    id: 'what-we-need-from-you',
    question: 'What information and access do you need from our community?',
    answer: `We'll guide you through providing materials at the right time:

**Within 14 Days of Payment (Required for Month 1 Kickoff):**
• Governing documents (bylaws, CC&Rs, policies, meeting minutes) - Digital format only (PDF/Word)
• Branding materials (logo in vector format preferred: AI, EPS, SVG; or high-res PNG/JPG 1000px+ wide)
• Brand colors (hex codes like #1E3A8A or Pantone values)
• Administrative contacts (Point of Contact with decision authority, initial admin list with names/roles/emails)

**By Month 5 (Data Migration Phase):**
• Current resident roster (Excel/CSV format):
  - Property addresses
  - Owner names (primary + secondary)
  - Email addresses and phone numbers
  - Parcel IDs
• Financial records (if you want financial transparency features):
  - Past 3 years financial statements (Excel format preferred)
  - Current budget with line-item categories
  - Asset inventory (Association-owned equipment/facilities)

**Digital Format Requirement - CRITICAL:**
• We do NOT accept paper documents
• You must scan them yourself or hire a scanning service
• Assessment fee: $500 if you need help planning conversion (we don't provide referrals unless you pay this fee)

**Data Organization Matters:**
• Included in base price: Up to 500 files OR 5 GB, well-organized, clear file names
• Extra fees apply: Extensive cleanup, files named "IMG_4729.jpg" or "untitled_scan_47.pdf", 10 versions of same document requiring de-duplication, volume overages (>500 files, >5GB, >1,000 residents)

**No System Access Required:**
• We do NOT need access to your existing systems
• We do NOT need bank account credentials
• You provide exports/files—we import them securely
• All data handled with encryption at rest and in transit

**Pro Tip - Avoid Extra Fees:**
• Rename files clearly: CATEGORY_YEAR_Description.pdf (e.g., Bylaws_2023.pdf, Minutes_Board_2024-03.pdf)
• Organize into folders: /Governing_Documents/, /Meeting_Minutes/, /Financial_Records/
• Delete duplicates before uploading
• Use clean spreadsheets (no merged cells, clear column headers)`,
    category: 'onboarding',
  },
  {
    id: 'rollout-strategy',
    question: 'How do we roll out the platform to our entire community?',
    answer: `Recommended approach: Test group first, then full community launch.

**Month 6: Soft Launch with Test Group (Recommended)**
• Select 10-20 tech-comfortable residents as early adopters
• Admins practice resident onboarding workflow
• Test signup link distribution and account creation
• Collect feedback and build admin confidence
• **Important:** Platform can support entire community from day one, test group is for YOUR comfort, not a platform limitation

**Month 7: Public Go-Live to All Residents**
• Distribute signup link to entire community (e.g., app.yourlandowners.org/signup)
• Distribution methods: Email, postal mail, website announcement, community meetings
• Residents self-register (no admin hand-holding required)
• Platform automatically creates accounts and profiles

**How Resident Onboarding Works (Super Simple):**
1. You send the signup link
2. Residents click link, create account, fill out profile
3. Residents start using platform (RSVP events, read news, view documents, vote)
4. Admins manage content (create events, post announcements, upload documents, run elections)

**30-Day Stabilization Support (Month 7):**
• Heightened support during initial resident onboarding wave
• Faster response times per your package tier (Gold: 4hr/12hr; Silver: 8hr/24hr)
• Proactive monitoring of platform performance
• Bug fixes and minor configuration adjustments

**What We DON'T Do:**
• Send signup links to residents for you (that's your communication strategy)
• Manage your community operations (run events, moderate posts, resolve disputes)
• Force resident adoption (we provide tools, you execute rollout campaign)
• Wait indefinitely until you "feel confident" (if platform works and CAN onboard residents, our onboarding is complete)

**Adoption:**
We do not have adoption statistics to share yet since this is a new platform. Adoption depends on how actively you promote platform usage and the features live at the time of your launch.`,
    category: 'onboarding',
  },
  {
    id: 'success-measurement',
    question: 'How do you measure and ensure successful implementation?',
    answer: `Onboarding is complete when the platform works, not when you "feel ready."

**✅ Our Obligation (What We Must Deliver):**
1. Platform Access: Admins can log in with credentials
2. Functional Tools: Every feature that is live at your launch works as designed; ask us for current build status on any feature before relying on it
3. Resident Signup: Admins can generate signup links and distribute to residents
4. Production-Ready Platform: Platform supports entire community population from day one (not limited to test group)
5. Training Materials: Guides and documentation delivered
6. Stabilization Support: 30-day heightened support begins at go-live

**❌ NOT Our Obligation (What We Don't Guarantee):**
1. Administrator Comfort Levels: We can't control whether you "feel ready" (subjective)
2. Onboarding Residents For You: We provide signup link; YOU distribute and manage communication
3. Managing Community Operations: We build tools; YOU run community (moderate posts, manage events)
4. Resident Adoption Rates: We can't force residents to sign up or engage
5. Emotional Readiness: Some admins feel overwhelmed by new technology—this doesn't mean onboarding is incomplete

**The "I Don't Feel Ready" Problem We Want to Avoid:**
• Month 6: Platform works perfectly. You test it, everything functions.
• Month 7: You say "We're not ready to launch. We don't feel comfortable yet."
• Month 8: We ask "What's broken?" You say "Nothing, we just feel overwhelmed."
• Month 9: We're stuck because you never feel "ready," but platform is done.

**Why Completion = FUNCTIONALITY, Not FEELINGS:**
If the platform works and you CAN onboard residents (even if nervous), we've completed our job. You can't hold us in perpetual onboarding because you're stressed—that's normal, and we'll support you, but it's not grounds to delay completion.

**What We Verify Before Calling Onboarding Complete:**
• Platform uptime and performance during onboarding
• Feature functionality testing results (UAT pass/fail)
• Administrator training completion
• Test group onboarding success (if you run one)
• 30-day post-launch stability and bug resolution rates

**On Adoption Results:**
This is a new platform, so we do not have adoption statistics or outcome guarantees to share. We do not track or promise resident adoption percentages, participation increases, or satisfaction scores; those depend on your community and your rollout, not on anything the platform measures automatically.`,
    category: 'onboarding',
  },
];
